import axios from 'axios';

const API_BASE = process.env.REACT_APP_BACK_URL || `${window.location.protocol}//${window.location.hostname}:5000`;

type PagoSocio = {
  pagosSocios_id: string;
  pagosSocios_anio: number;
  pagosSocios_periodo: number;
  pagosSocios_monto: number;
  pagosSocios_fechaVencimiento: string;
  pagosSocios_estado: number;
  pagosSocios_deleted: boolean; 
};

const NO_PAGOS_SOCIO_MESSAGE = "No se encontraron pagos del socio en SOS Contador";
const GENERIC_PAGOS_ERROR_MESSAGE = "Error al obtener los pagos del socio";

const isNoPaymentsCase = (payload: any, status?: number) => {
  const message = String(payload?.message || "").toLowerCase();
  if (status === 404) return true;
  if (message.includes("no se encontraron pagos")) return true;
  if (message.includes("error al obtener los pagos del socio")) return true;
  return false;
};

const normalizePagos = (source: any[]) => {
  return (source || [])
    .filter((item: any) => !item?.pagosSocios_deleted)
    .map((item: any) => ({
      id: item.pagosSocios_id ?? item.id,
      año: item.pagosSocios_anio,
      mes: getMonthFromPeriod(item.pagosSocios_periodo),
      importe: Number(item.pagosSocios_monto ?? 0),
      fechaVencimiento: formatDate(item.pagosSocios_fechaVencimiento || item.pagosSocios_fechaPago),
      estado:
        typeof item.pagosSocios_estado === 'number'
          ? getEstadoFromCodigo(item.pagosSocios_estado)
          : String(item.pagosSocios_estado || 'Por cobrar'),
    }));
};

export const getPagosSocios = async (id: number, getAccessTokenSilently: () => Promise<string>) => {
  const token = await getAccessTokenSilently();

  try {
    const response = await axios.get(`${API_BASE}/pagos-socios/socio/${id}`, {
      headers: { Authorization: `Bearer ${token}` },
    });

    if (isNoPaymentsCase(response?.data, response?.status)) return [];

    const source = Array.isArray(response.data) ? response.data : [];
    return normalizePagos(source);
  } catch (error: any) {
    const backendPayload = error?.response?.data;
    const status = error?.response?.status;

    if (isNoPaymentsCase(backendPayload, status)) {
      return [];
    }

    // Fallback: usar endpoint nuevo de detalle de socio con SOS
    try {
      const fallback = await axios.get(`${API_BASE}/socio/${id}/pagos`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { includeSos: true, includeSosMovimientos: false },
      });

      const localPagos = Array.isArray(fallback?.data?.pagos) ? fallback.data.pagos : [];
      const sosPagos = Array.isArray(fallback?.data?.pagos_sos) ? fallback.data.pagos_sos : [];
      return normalizePagos([...localPagos, ...sosPagos]);
    } catch {
      const backendMessage = backendPayload?.message;
      throw new Error(backendMessage || error?.message || GENERIC_PAGOS_ERROR_MESSAGE);
    }
  }
};

export const getDeudaSocio = async (id: number, getAccessTokenSilently: () => Promise<string>) => {
  const token = await getAccessTokenSilently();
  const { data } = await axios.get(
    `${API_BASE}/pagos-socios/deuda_socio/${id}`,
    {
      headers: { Authorization: `Bearer ${token}` },
    }
  );
  return data;
};

export const getSocioPagosConSos = async (
  id: number,
  getAccessTokenSilently: () => Promise<string>,
  options?: {
    includeSosMovimientos?: boolean;
    refreshSos?: boolean;
    fechaDesde?: string;
    fechaHasta?: string;
    cp?: 'C' | 'P';
    tipo?: 'T' | 'D' | 'H';
  },
) => {
  const token = await getAccessTokenSilently();
  const response = await axios.get(`${API_BASE}/socio/${id}/pagos`, {
    headers: { Authorization: `Bearer ${token}` },
    params: {
      includeSos: true,
      includeSosMovimientos: options?.includeSosMovimientos ?? true,
      refreshSos: options?.refreshSos,
      fechaDesde: options?.fechaDesde,
      fechaHasta: options?.fechaHasta,
      cp: options?.cp,
      tipo: options?.tipo,
    },
  });

  return response.data;
};

export const descargarFacturaPdf = async (
  movimiento: {
    fecha?: string;
    documento?: string;
    comprobante?: string;
    detalle?: string;
    tipo?: string;
    movimiento?: string;
    importe?: number;
    saldo?: number;
    estado?: string;
    socioNombre?: string;
    socioId?: number;
  },
  getAccessTokenSilently: () => Promise<string>,
) => {
  const token = await getAccessTokenSilently();
  const response = await axios.post(`${API_BASE}/movimientos/factura-pdf`, movimiento, {
    headers: { Authorization: `Bearer ${token}` },
    responseType: 'blob',
  });

  const blob = new Blob([response.data], { type: 'application/pdf' });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  const compSafe = (movimiento.comprobante || 'movimiento').replace(/[^a-zA-Z0-9-_]/g, '_');
  link.download = `movimiento-${compSafe}.pdf`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

const getEstadoFromCodigo = (codigo: number) => {
  switch (codigo) {
    case 0:
      return 'Pagada';
    case 1:
      return 'Por cobrar';
    case 2:
      return 'Adeudada';
    default:
      return 'Desconocido';
  }
};

const getMonthFromPeriod = (period: number) => {
  const meses = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
  return meses[period] || 'Mes desconocido';
};

const formatDate = (fecha: string) => {
  const date = new Date(fecha);
  return date.toLocaleDateString();
};
